/**
 * transcriptMerge - pure merge/publish logic for the live shared transcript.
 *
 * Every pane viewing a chat renders the merged stream of its local state and
 * the Firestore mirror; every pane publishes only what it authored. The rules
 * here are deliberately free of Firebase/React imports so they can be unit
 * tested directly and applied identically by the per-chat mirror, the
 * chat-list hydrate, and the sync flush.
 *
 * Core model:
 * - `timestamp` is IDENTITY: a message's creation time, never advanced by
 *   later rewrites. All panes sort by (timestamp, id), so ordering is
 *   identical everywhere.
 * - `rev` is FRESHNESS: bumped (Date.now) on every local write. Merges keep
 *   the higher rev; publishing pushes any copy whose rev is newer than the
 *   last rev published or observed for that message id.
 */

/**
 * Effective revision of a message copy. Messages written before rev stamping
 * existed (or restored from old localStorage) fall back to their creation
 * timestamp so they still publish once and compare sanely.
 */
export const effectiveRev = (message) => {
  if (typeof message?.rev === 'number' && message.rev > 0) return message.rev;
  const ms = Date.parse(message?.timestamp || '');
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Merge a chat's remote (Firestore) messages into the local copy.
 * Per-message rules:
 *   1. A message THIS pane is live-streaming keeps its local copy - the
 *      pane's own SSE state is the source of truth for its own turn.
 *   2. Otherwise the copy with the higher rev wins (freshest author copy).
 *   3. On equal revs the LOCAL copy is preferred (it is a superset of the
 *      synced schema), except a local copy stuck streaming while the remote
 *      has settled (stale reload state), where remote wins.
 * The result is sorted by (timestamp, id) and re-sequenced.
 *
 * @param {Array} localMessages
 * @param {Array} remoteMessages
 * @param {(id: string) => boolean} [isMessageOwned]
 * @returns {Array} merged, ordered, re-sequenced messages
 */
export const mergeRemoteMessages = (localMessages = [], remoteMessages = [], isMessageOwned = () => false) => {
  const byId = new Map();
  for (const m of localMessages || []) {
    if (m?.id) byId.set(m.id, m);
  }
  for (const remote of remoteMessages || []) {
    if (!remote?.id) continue;
    const local = byId.get(remote.id);
    if (!local) {
      byId.set(remote.id, remote);
      continue;
    }
    if (isMessageOwned(remote.id)) continue;
    const localRev = typeof local.rev === 'number' ? local.rev : 0;
    const remoteRev = typeof remote.rev === 'number' ? remote.rev : 0;
    if (remoteRev > localRev) {
      byId.set(remote.id, remote);
      continue;
    }
    if (remoteRev < localRev) continue;
    if (local.metadata?.isStreaming && !remote.metadata?.isStreaming) {
      byId.set(remote.id, remote);
    }
  }

  return Array.from(byId.values())
    .sort((a, b) => {
      const ta = Date.parse(a.timestamp || '') || 0;
      const tb = Date.parse(b.timestamp || '') || 0;
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    })
    .map((m, index) => ({ ...m, sequence: index }));
};

/**
 * Order a merged transcript.
 *
 * Server write order wins whenever it exists, because timestamps cannot be
 * compared across the two sources this function merges. A persisted message
 * is stamped by the SERVER when it is written; a message this pane is still
 * streaming is stamped by the CLIENT when the bubble opens. Those are two
 * clocks and two different moments in the turn, so a user message can easily
 * carry a later timestamp than the assistant reply it caused. That is exactly
 * what shipped: the user's own prompt rendered underneath the answer to it.
 *
 * A message with no server sequence has not been persisted yet, which by
 * definition makes it newer than everything that has, so it sorts last.
 */
const compareTranscriptOrder = (a, b) => {
  const seqA = Number.isSafeInteger(a.serverSequence) ? a.serverSequence : null;
  const seqB = Number.isSafeInteger(b.serverSequence) ? b.serverSequence : null;
  if (seqA !== null && seqB !== null) {
    if (seqA !== seqB) return seqA - seqB;
    return String(a.id).localeCompare(String(b.id));
  }
  if (seqA !== null) return -1;
  if (seqB !== null) return 1;
  // Neither is persisted, so both were created by this pane on one clock and
  // comparing their timestamps is meaningful.
  const timeA = Date.parse(a.timestamp || '') || 0;
  const timeB = Date.parse(b.timestamp || '') || 0;
  if (timeA !== timeB) return timeA - timeB;
  return String(a.id).localeCompare(String(b.id));
};

/**
 * Apply a full authoritative snapshot while preserving only messages this
 * pane is actively streaming. Unowned local messages are discarded.
 */
export const mergeAuthoritativeMessages = (
  remoteMessages = [],
  localMessages = [],
  isMessageOwned = () => false
) => {
  const byId = new Map();
  for (const message of remoteMessages || []) {
    if (!message?.id) continue;
    // Capture the server's write order NOW. The display `sequence` below is
    // overwritten with an array index every merge, so reading it on a later
    // pass would compare a server sequence against an index and reintroduce
    // the bug through a different door.
    const serverSequence = Number.isSafeInteger(message.serverSequence)
      ? message.serverSequence
      : (Number.isSafeInteger(message.sequence) ? message.sequence : undefined);
    byId.set(message.id, { ...message, serverSequence });
  }
  for (const message of localMessages || []) {
    if (message?.id && isMessageOwned(message.id)) {
      // A local message keeps any server order it earned on an earlier pass.
      // Losing it here would make an already-persisted message look live and
      // jump to the end of the transcript.
      const existing = byId.get(message.id);
      byId.set(message.id, {
        ...message,
        serverSequence: Number.isSafeInteger(message.serverSequence)
          ? message.serverSequence
          : existing?.serverSequence,
      });
    }
  }
  return Array.from(byId.values())
    .sort(compareTranscriptOrder)
    .map((message, index) => ({ ...message, sequence: index }));
};

/**
 * Select which messages a flush should publish to Firestore.
 *
 * Publish a message when its effective rev is newer than the last rev this
 * pane published (or observed arriving from remote) for that id. This is a
 * PER-MESSAGE cursor, deliberately not a chat-level watermark: a chat-level
 * timestamp watermark let any later message (for example a peer status line
 * written mid-turn) advance past an older still-streaming bubble, after
 * which that bubble's updates - including its final settled copy - never
 * published again.
 *
 * A streaming message another pane authors is never published from here,
 * even if bookkeeping is cold (fresh reload): republishing a stale copy of
 * a peer's live turn would clobber it remotely.
 *
 * @param {Array} messages - the chat's current local messages
 * @param {Map<string, number>} publishedRevs - per-message publish cursor
 * @param {(id: string) => boolean} [isMessageOwned]
 * @returns {Array} messages to write
 */
export const selectMessagesToPublish = (messages = [], publishedRevs, isMessageOwned = () => false) => {
  const cursor = publishedRevs instanceof Map ? publishedRevs : new Map();
  return (messages || []).filter((m) => {
    if (!m?.id) return false;
    if (m.metadata?.isStreaming && !isMessageOwned(m.id)) return false;
    return effectiveRev(m) > (cursor.get(m.id) || 0);
  });
};

/**
 * Record message copies as published/observed so the flush cursor skips them.
 * Used after a flush writes messages AND when remote copies arrive (mirror or
 * hydrate) so downloaded data is never echoed straight back.
 */
export const recordPublishedRevs = (publishedRevs, messages = []) => {
  if (!(publishedRevs instanceof Map)) return;
  for (const m of messages || []) {
    if (!m?.id) continue;
    const rev = effectiveRev(m);
    const prior = publishedRevs.get(m.id) || 0;
    if (rev > prior) publishedRevs.set(m.id, rev);
  }
};

/**
 * Fingerprint of the NON-TIMESTAMP fields the chat meta doc carries. The
 * sync flush skips the meta write while it is unchanged (upsertChat still
 * refreshes meta whenever a settled message publishes, which covers
 * lastMessageAt), and remote-driven merges prime it so a pane never echoes
 * metadata for a chat it merely mirrors (a follower writing its seeded
 * "New chat" meta would race the author pane's generated title).
 */
export const chatMetaFingerprint = (chat) => JSON.stringify([
  chat?.name || chat?.title || '',
  chat?.archived === true,
  chat?.pinned === true,
  chat?.origin || null,
  chat?.conductorMinted === true,
  chat?.transcriptMode || null,
  chat?.transcriptMode === 'locker_scoped' && Number.isInteger(chat?.messageCount)
    ? chat.messageCount
    : null
]);
